import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * Playback hook with graceful degradation:
 *   1. POST /api/v1/tts/speak (BFF proxies ElevenLabs with Redis cache).
 *   2. Fallback to window.speechSynthesis if the proxy returns 503 (no key).
 *   3. Keeps an in-memory ObjectURL cache so the same phrase doesn't re-request.
 *
 * Race safety: every call bumps `seqRef`. If a newer call fires before the
 * previous one starts playback, the stale call discards its result. This kills
 * the "áudio duplicado" symptom that happens when React 18 strict mode fires
 * the effect twice or the parent re-renders mid-fetch.
 */
export function useTts() {
    const [state, setState] = useState('idle');
    const [error, setError] = useState(null);
    const cacheRef = useRef(new Map());
    const audioRef = useRef(null);
    const seqRef = useRef(0);
    const stop = useCallback(() => {
        if (audioRef.current) {
            try {
                audioRef.current.pause();
                audioRef.current.currentTime = 0;
            }
            catch {
                // ignore
            }
            audioRef.current = null;
        }
        try {
            window.speechSynthesis?.cancel();
        }
        catch {
            // ignore
        }
        setState('idle');
    }, []);
    // Stop any dangling audio when the component unmounts.
    useEffect(() => {
        return () => {
            stop();
        };
    }, [stop]);
    const play = useCallback(async (text, voiceId, lang) => {
        if (!text)
            return;
        const mySeq = seqRef.current + 1;
        seqRef.current = mySeq;
        stop();
        setError(null);
        setState('loading');
        const cacheKey = `${voiceId ?? 'default'}::${text}`;
        const cached = cacheRef.current.get(cacheKey);
        if (cached) {
            if (seqRef.current !== mySeq)
                return;
            await playUrl(cached, mySeq);
            return;
        }
        try {
            const res = await fetch('/api/v1/tts/speak', {
                method: 'POST',
                credentials: 'include',
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify({ text, voiceId }),
            });
            if (seqRef.current !== mySeq)
                return;
            if (res.status === 503) {
                fallback(text, mySeq);
                return;
            }
            if (!res.ok)
                throw new Error(`TTS ${res.status}`);
            const blob = await res.blob();
            if (seqRef.current !== mySeq)
                return;
            const url = URL.createObjectURL(blob);
            cacheRef.current.set(cacheKey, url);
            await playUrl(url, mySeq);
        }
        catch (err) {
            if (seqRef.current !== mySeq)
                return;
            console.warn('tts failed — falling back', err);
            fallback(text, mySeq);
        }
        async function playUrl(url, seq) {
            if (seqRef.current !== seq)
                return;
            const audio = new Audio(url);
            audioRef.current = audio;
            audio.addEventListener('playing', () => {
                if (seqRef.current === seq)
                    setState('playing');
            });
            audio.addEventListener('ended', () => {
                if (seqRef.current === seq)
                    setState('idle');
            });
            audio.addEventListener('error', () => {
                if (seqRef.current !== seq)
                    return;
                setState('error');
                setError('Falha ao tocar áudio');
            });
            try {
                await audio.play();
            }
            catch (err) {
                if (seqRef.current !== seq)
                    return;
                setError(err.message);
                setState('error');
            }
        }
        function fallback(t, seq) {
            if (seqRef.current !== seq)
                return;
            if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
                setState('error');
                setError('TTS não disponível neste navegador');
                return;
            }
            const targetLang = lang ?? 'en-US';
            const utter = new SpeechSynthesisUtterance(t);
            utter.lang = targetLang;
            utter.rate = 0.92;
            // Tenta escolher a melhor voz disponível pro idioma alvo.
            // No PT-BR, prefere vozes Luciana (macOS), Google português,
            // Microsoft Maria (Windows), que costumam ser mais naturais.
            const voices = window.speechSynthesis.getVoices();
            if (voices.length > 0) {
                const matches = voices.filter((v) => v.lang.toLowerCase().startsWith(targetLang.slice(0, 2).toLowerCase()));
                const preferredNames = targetLang.startsWith('pt')
                    ? ['Luciana', 'Google português', 'Maria', 'Francisca', 'Helena', 'Joana']
                    : ['Samantha', 'Google US English', 'Microsoft Zira'];
                const picked = preferredNames
                    .map((n) => matches.find((v) => v.name.includes(n)))
                    .find(Boolean) ?? matches[0];
                if (picked)
                    utter.voice = picked;
            }
            utter.onend = () => {
                if (seqRef.current === seq)
                    setState('idle');
            };
            utter.onerror = () => {
                if (seqRef.current === seq)
                    setState('error');
            };
            setState('playing');
            window.speechSynthesis.speak(utter);
        }
    }, [stop]);
    return { play, stop, state, error };
}
//# sourceMappingURL=useTts.js.map